/**
 * Per-currency minimum fundraising targets on an event PlaceHub program.
 *
 * The event program carries one minimum per currency (`currencyTargets`),
 * with `fundraiserMinimumTargetAmount` kept as the amount in the event's own
 * currency. These helpers mirror the resolution rule the API applies, so the
 * UI can block a charity whose currency has no minimum before any request is
 * made.
 */

export interface CurrencyTarget {
  currency: string;
  fundraiserMinimumTargetAmount: number | null;
}

export interface EventProgramMinimumTargets {
  currency?: string | null;
  fundraiserMinimumTargetAmount?: number | null;
  currencyTargets?: CurrencyTarget[] | null;
}

/** Currencies of the countries a charity can register under. */
const baseAmbassadorProgramCurrencies = ['AUD', 'NZD', 'USD', 'CAD'];
const ukCharityCurrency = 'GBP';

export const getAmbassadorProgramCurrencies = (
  isUkCharityEnabled: boolean
): string[] =>
  isUkCharityEnabled
    ? [...baseAmbassadorProgramCurrencies, ukCharityCurrency]
    : [...baseAmbassadorProgramCurrencies];

export const normaliseCurrency = (code?: string | null): string | null => {
  if (!code) {
    return null;
  }
  const trimmed = code.trim().toUpperCase();
  return trimmed || null;
};

const toTargets = (targets: unknown): CurrencyTarget[] => {
  if (!Array.isArray(targets)) {
    return [];
  }
  return targets
    .filter((target) => target && normaliseCurrency(target.currency))
    .map((target) => ({
      currency: normaliseCurrency(target.currency) as string,
      fundraiserMinimumTargetAmount:
        typeof target.fundraiserMinimumTargetAmount === 'number' &&
        !Number.isNaN(target.fundraiserMinimumTargetAmount)
          ? target.fundraiserMinimumTargetAmount
          : null
    }));
};

/** Event currency first, then the rest in the order they were added. */
export const orderCurrencyTargets = (
  targets: unknown,
  eventCurrency?: string | null
): CurrencyTarget[] => {
  const list = toTargets(targets);
  const code = normaliseCurrency(eventCurrency);
  if (!code) {
    return list;
  }
  return [
    ...list.filter((target) => target.currency === code),
    ...list.filter((target) => target.currency !== code)
  ];
};

export const upsertCurrencyTarget = (
  targets: unknown,
  currency: string,
  amount: number | null
): CurrencyTarget[] => {
  const code = normaliseCurrency(currency);
  const list = toTargets(targets);
  if (!code) {
    return list;
  }
  const index = list.findIndex((target) => target.currency === code);
  if (index === -1) {
    return [...list, { currency: code, fundraiserMinimumTargetAmount: amount }];
  }
  return list.map((target, position) =>
    position === index
      ? { ...target, fundraiserMinimumTargetAmount: amount }
      : target
  );
};

export const removeCurrencyTarget = (
  targets: unknown,
  currency: string
): CurrencyTarget[] => {
  const code = normaliseCurrency(currency);
  return toTargets(targets).filter((target) => target.currency !== code);
};

/**
 * The minimum an event program sets for a currency: the matching row, else
 * the event-currency amount when the currency is the event's own, else none.
 */
export const getEventProgramMinimumTarget = (
  program: EventProgramMinimumTargets | null | undefined,
  currency?: string | null
): number | null => {
  const code = normaliseCurrency(currency);
  if (!program || !code) {
    return null;
  }

  const row = toTargets(program.currencyTargets).find(
    (target) => target.currency === code
  );
  if (row && row.fundraiserMinimumTargetAmount !== null) {
    return row.fundraiserMinimumTargetAmount;
  }

  if (
    normaliseCurrency(program.currency) === code &&
    typeof program.fundraiserMinimumTargetAmount === 'number'
  ) {
    return program.fundraiserMinimumTargetAmount;
  }

  return null;
};

export const programUsesMinimumTargets = (
  program: EventProgramMinimumTargets | null | undefined
): boolean => {
  if (!program) {
    return false;
  }
  if (typeof program.fundraiserMinimumTargetAmount === 'number') {
    return true;
  }
  return toTargets(program.currencyTargets).some(
    (target) => target.fundraiserMinimumTargetAmount !== null
  );
};

/**
 * Whether a charity in `currency` can be added to the program. A program
 * without any minimum accepts every charity. An unknown charity currency is
 * left for the API to judge.
 */
export const isOrganizationCurrencyConfigured = (
  program: EventProgramMinimumTargets | null | undefined,
  currency?: string | null
): boolean => {
  if (!programUsesMinimumTargets(program)) {
    return true;
  }
  if (!normaliseCurrency(currency)) {
    return true;
  }
  return getEventProgramMinimumTarget(program, currency) !== null;
};
